class CupPromotionTracker {
    constructor() {
      this.promotionBanner = null;
      this.availableCupVariants = []; // Store cup variant options
      this.defaultCupVariant = null;
      this.isUpdating = false; // Race condition protection for form submissions
      this.isUpdatingPromotion = false; // Race condition protection for update cycle
      this.modalUpdateTimeout = null; // Debounce modal updates
      this.updateTimeout = null; // Overall update timeout
      this.stuckStateRecoveryTimeout = null; // Recovery mechanism
      this.productTagCache = new Map(); // Cache product tags to avoid duplicate fetches
      this.cartDataCache = { data: null, fetchedAt: 0 };
      this.cacheTTL = 1500; // ms cache window to reduce duplicate fetches
      this.debugEnabled = Boolean(window?.__CUP_PROMOTION_DEBUG__);
      this.init();
      this.cupQualificationTag = 'tier-sale';
      this.freeCupTag = 'free-cup';
      this.isUSMarket = false;
      this.validRegions = ['eu', 'au']; // Regional tags (products without these = 'international' / US)
    }

    logDebug(...args) {
      if (!this.debugEnabled) return;
      console.log(...args);
    }
  
    async init() {
      // Wait for cart drawer to be available (like tier-sale does)
      setTimeout(() => {
        this.checkAndInitialize();
        this.setupEventListeners();
      }, 500);
    }

    checkAndInitialize() {
      if (!this.isCupPromotionEnabled()) {
        // Listen for cart drawer to load
        document.addEventListener('theme:cart:loaded', () => {
          this.checkAndInitialize();
        });
        return;
      }

      this.continueInit();
    }

    isCupPromotionEnabled() {
      // Check if the cup promotion feature is enabled in theme settings
      // This checks if the progress bar element exists, which means the Liquid template rendered it
      // The Liquid template only renders if settings.enable_cup_promotion is true
      return document.querySelector('[data-cup-promotion]') !== null;
    }

  async continueInit() {
    this.promotionBanner = document.querySelector('[data-cup-promotion]');
    if (this.promotionBanner) {
      const currencyCode = this.promotionBanner.getAttribute('data-currency-code');
      // Simple: USD = US/local, anything else = international/non-US
      this.isUSMarket = currencyCode === 'USD';
    }
    
    // Load cup variant options from theme settings or collections
    await this.loadCupVariants();
    
    // Update promotion status once
    this.updatePromotionStatus();
  }
  
    async loadCupVariants() {
      // Products are already loaded by Liquid template in the DOM
      // No need to fetch via AJAX - just check if products are available
      const productsContainer = document.querySelector('.cup-variant-selector__products');
      const productElements = productsContainer?.querySelectorAll('.cup-variant-product');
      
      if (productElements && productElements.length > 0) {
        // Products are available in the DOM
        this.availableCupVariants = Array.from(productElements);
        this.defaultCupVariant = this.availableCupVariants[0] || null;
      } else {
        console.warn('⚠️ No cup products found. Please configure a collection in Theme Settings > Buy X Get Y Promotion > Free Cup Collection');
        this.availableCupVariants = [];
        this.defaultCupVariant = null;
      }
    }

  
    setupEventListeners() {
      // Theme-specific cart events (exact match with tier-sale)
      const cartEvents = [
        'theme:cart:added',
        'theme:cart:loaded', 
        'theme:cart:updated',
        'cart:updated',
        'cart:item-added',
        'cart:item-removed'
      ];
      
      cartEvents.forEach(eventName => {
        document.addEventListener(eventName, () => {
          this.handleCartChange();
        });
      });
  
      // Cart drawer interactions (exact match with tier-sale)
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-quantity-button], [data-item-remove], [data-cart-item-remove], .cart-drawer__item-remove, .cart__item-remove, [data-remove]')) {
          this.handleCartChange(500);
        }
        
        if (e.target.matches('[data-add-to-cart], button[type="submit"]') && !e.target.closest('[data-cup-variant-form]')) {
          this.handleCartChange(800);
        }

        // Add free cups button
        if (e.target.matches('[data-add-free-cups]')) {
          e.preventDefault();
          this.handleAddFreeCups();
        }

        // Browse tubs button
        if (e.target.matches('[data-browse-tubs]')) {
          e.preventDefault();
          this.handleBrowseTubs();
        }

        // Cup selector close button and backdrop
        if (e.target.matches('[data-cup-selector-close], [data-cup-selector-backdrop]') || e.target.closest('[data-cup-selector-close]')) {
          this.hideCupSelector();
        }
      });

      // Quantity changes (exact match with tier-sale)
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="updates[]"], [data-quantity-field]')) {
          this.handleCartChange(300);
        }
      });

      // Cup variant add button - show loading on click (theme handles the actual add-to-cart)
      document.addEventListener('click', (e) => {
        const addButton = e.target.closest('[data-cup-variant-add]');
        if (!addButton) return;

        const product = addButton.closest('.cup-variant-product');
        if (!product) return;

        // Track which button was clicked so we can update it on cart:added
        this.lastClickedCupProduct = product;
        this.lastClickedCupButton = addButton;
        this.isUpdating = true;

        // Show loading immediately
        this.showCupLoading(product, addButton);

        // Safety timeout: if theme:cart:added never fires, recover after 8s
        clearTimeout(this.cupAddSafetyTimeout);
        this.cupAddSafetyTimeout = setTimeout(() => {
          if (this.isUpdating && this.lastClickedCupButton) {
            this.resetCupState(this.lastClickedCupProduct, this.lastClickedCupButton);
            this.lastClickedCupProduct = null;
            this.lastClickedCupButton = null;
            this.isUpdating = false;
          }
        }, 8000);
      });

      // When theme confirms item was added, show success then reset to allow adding more
      document.addEventListener('theme:cart:added', () => {
        if (this.lastClickedCupButton && this.lastClickedCupProduct) {
          clearTimeout(this.cupAddSafetyTimeout);
          const product = this.lastClickedCupProduct;
          const button = this.lastClickedCupButton;

          this.showCupSuccess(product, button);

          setTimeout(() => {
            this.resetCupState(product, button);
            this.lastClickedCupProduct = null;
            this.lastClickedCupButton = null;
            this.isUpdating = false;
          }, 1500);
        }
      });

      // Cup quantity controls
      document.addEventListener('click', (e) => {
        if (e.target.matches('[data-quantity-plus], [data-quantity-minus]')) {
          e.preventDefault();
          this.handleQuantityChange(e.target);
        }
      });

      // Handle escape key to close cup selector
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const selector = document.querySelector('[data-cup-variant-selector].is-active');
          if (selector) {
            this.hideCupSelector();
          }
        }
      });

      // MutationObserver for DOM changes (exact match with tier-sale)
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            const target = mutation.target;
            if (target.closest('.cart-drawer__items, .cart__items, #cartItemsHolder')) {
              shouldUpdate = true;
            }
          }
        });
        
        if (shouldUpdate) {
          this.handleCartChange(200);
        }
      });

      // Set up the observer
      const cartContainer = document.querySelector('.cart-drawer__items, .cart__items, #cartItemsHolder');
      if (cartContainer) {
        observer.observe(cartContainer, {
          childList: true,
          subtree: true
        });
      }
    }
  
    async handleCartChange(delay = 300) {
      // Race condition protection
      if (this.isUpdatingPromotion) {
        this.logDebug('⚠️ Promotion update already in progress, debouncing...');
      }

      // Invalidate cart cache on incoming cart change to ensure freshness
      this.cartDataCache = { data: null, fetchedAt: 0 };

      // Clear any existing timeouts
      this.clearAllTimeouts();

      // Set up stuck state recovery
      this.stuckStateRecoveryTimeout = setTimeout(() => {
        console.error('⚠️ Cup promotion update appears stuck, forcing recovery...');
        this.forceRecoveryFromStuckState();
      }, 15000);

      this.cartChangeTimeout = setTimeout(async () => {
        try {
          this.isUpdatingPromotion = true;

          this.updateTimeout = setTimeout(() => {
            console.error('⚠️ Cup promotion update timeout exceeded, forcing cleanup...');
            this.forceRecoveryFromStuckState();
          }, 10000);

          await this.updatePromotionStatus();

          // If modal is open, also refresh modal availability
          const isModalOpen = document.querySelector('.cup-variant-selector.is-active');
          if (isModalOpen) {
            await this.refreshModalAvailability();
          }

          this.clearUpdateState();
        } catch (error) {
          console.error('Error in cart change handler:', error);
          this.forceRecoveryFromStuckState();
        }
      }, delay);
    }

    clearUpdateState() {
      this.isUpdatingPromotion = false;
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      if (this.stuckStateRecoveryTimeout) {
        clearTimeout(this.stuckStateRecoveryTimeout);
        this.stuckStateRecoveryTimeout = null;
      }
    }

    clearAllTimeouts() {
      if (this.cartChangeTimeout) {
        clearTimeout(this.cartChangeTimeout);
        this.cartChangeTimeout = null;
      }
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
        this.updateTimeout = null;
      }
      if (this.stuckStateRecoveryTimeout) {
        clearTimeout(this.stuckStateRecoveryTimeout);
        this.stuckStateRecoveryTimeout = null;
      }
    }

    forceRecoveryFromStuckState() {
      this.logDebug('🚨 Forcing recovery from stuck state...');
      this.clearAllTimeouts();
      clearTimeout(this.cupAddSafetyTimeout);
      this.clearUpdateState();
      this.hideModalLoading();

      // Reset any stuck cup button state
      if (this.lastClickedCupButton && this.lastClickedCupProduct) {
        this.resetCupState(this.lastClickedCupProduct, this.lastClickedCupButton);
        this.lastClickedCupProduct = null;
        this.lastClickedCupButton = null;
      }
      this.isUpdating = false;

      // Try a gentle retry after a delay
      setTimeout(() => {
        if (!this.isUpdatingPromotion) {
          this.updatePromotionStatus().catch(error => {
            this.logDebug('⚠️ Gentle retry failed:', error.message);
          });
        }
      }, 2000);
    }
  
    async updatePromotionStatus() {
      try {
        this.promotionBanner = document.querySelector('[data-cup-promotion]');
        if (!this.promotionBanner) {
          // console.warn('⚠️ Cup promotion banner not found in DOM - feature may be disabled');
          return;
        }
  
        const cartData = await this.getCartData();
        if (!cartData) return;
  
      const tubCount = await this.countTubItems(cartData);
      const cupCount = await this.countCupItems(cartData);
        const cupsAvailable = Math.max(0, tubCount - cupCount);

  
        await this.updatePromotionUI(tubCount, cupCount, cupsAvailable);
        
      } catch (error) {
        console.error('Error updating cup promotion:', error);
      }
    }
  
    async countTubItems(cartData) {
      let totalTubs = 0;
      
      for (const item of cartData.items) {
        const hasQualifyingTag = await this.checkProductHasQualifyingTag(item);
        if (hasQualifyingTag) {
          totalTubs += item.quantity;
        }
      }
      
      return totalTubs;
    }

    async detectCartRegions(cartData) {
      // Detect which regions are present in qualifying tub items
      const regions = new Set();
      
      for (const item of cartData.items) {
        const hasQualifyingTag = await this.checkProductHasQualifyingTag(item);
        if (hasQualifyingTag) {
          // Check for region tags on the product
          const productRegion = await this.getProductRegion(item);
          if (productRegion) {
            regions.add(productRegion);
          }
        }
      }
      
      return Array.from(regions);
    }

    async getProductRegion(cartItem) {
      // Extract region from product tags (e.g., 'eu', 'au')
      // Products without these tags are considered 'international' (US)
      try {
        const tags = await this.getProductTags(cartItem);

        for (const tag of tags) {
          const lowerTag = tag.toLowerCase();
          if (this.validRegions.includes(lowerTag)) {
            return lowerTag;
          }
        }

        return 'international';
      } catch (error) {
        this.logDebug('⚠️ Could not detect region for product:', cartItem.handle, error.message);
        return 'international';
      }
    }

    async countCupItems(cartData) {
      let totalCups = 0;
      
      for (const item of cartData.items) {
        const hasCupTag = await this.checkProductHasCupTag(item);
        if (hasCupTag) {
          totalCups += item.quantity;
        }
      }
      
      return totalCups;
    }

    async getProductTags(cartItem) {
      try {
        if (cartItem?.product_tags) {
          return cartItem.product_tags;
        }

        const productHandle = cartItem?.handle;
        if (!productHandle) return [];

        if (this.productTagCache.has(productHandle)) {
          return this.productTagCache.get(productHandle);
        }

        const response = await this.fetchWithTimeout(`/products/${productHandle}.js`, {}, 3000);
        if (!response.ok) return [];

        const productData = await response.json();
        const tags = productData.tags || [];
        this.productTagCache.set(productHandle, tags);
        return tags;
      } catch (error) {
        this.logDebug('⚠️ Could not get tags for product:', cartItem?.handle, error.message);
        return [];
      }
    }

    async checkProductHasQualifyingTag(cartItem) {
      const tags = await this.getProductTags(cartItem);
      return tags.includes(this.cupQualificationTag);
    }

    async checkProductHasCupTag(cartItem) {
      const tags = await this.getProductTags(cartItem);
      return tags.includes(this.freeCupTag);
    }

    // Helper method for singular/plural cup text formatting
    formatCupText(count, options = {}) {
      const { 
        singular = 'cup', 
        plural = 'cups',
        capitalize = false,
        includeCount = true 
      } = options;
      
      const word = count === 1 ? singular : plural;
      const finalWord = capitalize ? word.charAt(0).toUpperCase() + word.slice(1) : word;
      
      return includeCount ? `${count} ${finalWord}` : finalWord;
    }

    // Helper method for fetch with timeout (matching tier-sale pattern)
    async fetchWithTimeout(url, options = {}, timeout = 5000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error(`Request timeout after ${timeout}ms`);
        }
        throw error;
      }
    }
  
    async updatePromotionUI(tubCount, cupCount, cupsAvailable) {
    // Use JavaScript data directly - no fallback to server data
    // This ensures the UI reflects the actual cart state

      this.promotionBanner.setAttribute('data-tub-items', tubCount);
      this.promotionBanner.setAttribute('data-cup-items', cupCount);
      this.promotionBanner.setAttribute('data-cups-available', cupsAvailable);
  
    // Always show promotion banner to incentivize customers
    this.promotionBanner.style.display = 'block';

    // Select descriptions based on currency: USD = US/local, anything else = international/non-US
    const description = this.isUSMarket 
      ? this.promotionBanner.getAttribute('data-local-description') 
      : this.promotionBanner.getAttribute('data-international-description');
    
    const subDescription = this.isUSMarket 
      ? this.promotionBanner.getAttribute('data-local-sub-description') 
      : this.promotionBanner.getAttribute('data-international-sub-description');
    
    // Update title
    const titleElement = this.promotionBanner.querySelector('.cup-promotion-progress__title');
    if (titleElement) {
      titleElement.textContent = description || '🎉 FREE Cup with Every Tub!';
    }
    
    // Update status
    const statusText = this.promotionBanner.querySelector('[data-cup-status-text]');
    if (statusText) {
      if (tubCount === 0) {
        statusText.textContent = subDescription || 'Add a qualifying product to get your free cup!';
      } else if (cupsAvailable > 0) {
        statusText.textContent = 'You have qualifying items in your cart!';
      } else {
        const cupText = this.formatCupText(cupCount, { capitalize: false });
        statusText.textContent = `${cupText} added to your cart 🎉`;
      }
    }

    // Update alert sections
    const cupsAlert = this.promotionBanner.querySelector('.cup-promotion-progress__cups-alert');
    const browseAlert = this.promotionBanner.querySelector('.cup-promotion-progress__browse-alert');
    const promotionVisualFlow = this.promotionBanner.querySelector('.promotion-visual-flow-wrapper');
    if (cupsAlert) {
      cupsAlert.style.display = (tubCount > 0 && cupsAvailable > 0) ? 'flex' : 'none';
    }
    if (browseAlert) {
      // Only show browse alert when there are qualifying items and cups available
      // This will be used for cup selection functionality later
      browseAlert.style.display = (tubCount > 0 && cupsAvailable > 0) ? 'flex' : 'none';
    }
    if (promotionVisualFlow) {
      promotionVisualFlow.style.visibility = (tubCount === 0) ? 'visible' : 'hidden';
      promotionVisualFlow.style.height = (tubCount === 0) ? 'auto' : '0';
    }

    // Update button text (always "Claim Now") and counts
    const addButton = this.promotionBanner.querySelector('[data-add-free-cups]');
    const cupsTextElement = this.promotionBanner.querySelector('.cup-promotion-progress__cups-text');
    
    if (addButton && cupsAvailable > 0) {
      addButton.textContent = 'Claim Now';
      addButton.disabled = false;
    }
    
    if (cupsTextElement && cupsAvailable > 0) {
      const cupText = this.formatCupText(cupsAvailable, { includeCount: false });
      cupsTextElement.innerHTML = `<span class="cup-promotion-progress__cups-count">${cupsAvailable}</span> free ${cupText} available`;
      }
    }
  
    async     handleAddFreeCups() {
      if (!this.availableCupVariants || this.availableCupVariants.length === 0) {
        console.warn('⚠️ No free cup products configured. Please configure a collection for free cup products in Theme Settings > Buy X Get Y Promotion > Free Cup Collection.');
      }
      this.showCupSelector();
    }

  async showCupSelector() {
    const selector = document.querySelector('[data-cup-variant-selector]');
    if (!selector) return;
    
    selector.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    
    // Hide empty state immediately to prevent flickering
    const noCupsContainer = selector.querySelector('[data-no-cups-container]');
    if (noCupsContainer) {
      noCupsContainer.style.display = 'none';
    }
    
    // Immediately refresh modal state when opening (with region filtering)
    setTimeout(() => {
      this.refreshModalAvailability();
    }, 50);
    
    // Focus management for accessibility
    const closeButton = selector.querySelector('[data-cup-selector-close]');
    if (closeButton) {
      setTimeout(() => closeButton.focus(), 150);
    }
  }

    hideCupSelector() {
      const selector = document.querySelector('[data-cup-variant-selector]');
      if (!selector) return;
      
      selector.classList.remove('is-active');
      document.body.style.overflow = '';
      
      // Return focus to choose cups button
      const chooseButton = document.querySelector('[data-add-free-cups]');
      if (chooseButton) {
        chooseButton.focus();
      }
    }


    handleQuantityChange(button) {
      if (!button) return;
      
      const product = button.closest('.cup-variant-product');
      if (!product) return;
      
      const input = product.querySelector('[data-quantity-input]');
      if (!input) return;
      
      const isPlus = button.matches('[data-quantity-plus]');
      const isMinus = button.matches('[data-quantity-minus]');
      
      let currentValue = parseInt(input.value) || 1;
      const maxValue = parseInt(input.max) || 1;
      
      if (isPlus && currentValue < maxValue) {
        input.value = currentValue + 1;
      } else if (isMinus && currentValue > 1) {
        input.value = currentValue - 1;
      }
      
      // Update form quantity
      const form = product.querySelector('[data-cup-variant-form]');
      if (form) {
        const quantityInput = form.querySelector('input[name="quantity"]');
        if (quantityInput) {
          quantityInput.value = input.value;
        }
      }
    }

    showCupLoading(product, button) {
      if (!button || !product) return;

      button.classList.add('cup-variant-product__add-btn--loading');
      button.disabled = true;

      const buttonText = button.querySelector('.cup-variant-product__add-text');
      if (buttonText) buttonText.textContent = 'Adding...';
    }

    showCupSuccess(product, button) {
      if (!button || !product) return;

      button.classList.remove('cup-variant-product__add-btn--loading');
      button.classList.add('cup-variant-product__add-btn--success');

      const buttonText = button.querySelector('.cup-variant-product__add-text');
      if (buttonText) buttonText.textContent = 'Added ✓';
    }

    setCupAdded(product, button) {
      if (!button || !product) return;

      button.classList.remove('cup-variant-product__add-btn--loading', 'cup-variant-product__add-btn--success');
      button.classList.add('cup-variant-product__add-btn--added');
      button.disabled = true;

      const buttonText = button.querySelector('.cup-variant-product__add-text');
      if (buttonText) buttonText.textContent = 'Added';
    }

    showCupError(product, button, errorMessage) {
      if (!button || !product) return;

      button.classList.remove('cup-variant-product__add-btn--loading');
      button.classList.add('cup-variant-product__add-btn--error');

      const buttonText = button.querySelector('.cup-variant-product__add-text');
      if (buttonText) buttonText.textContent = 'Error';
    }

    resetCupState(product, button) {
      if (!button || !product) return;
      
      button.classList.remove(
        'cup-variant-product__add-btn--loading',
        'cup-variant-product__add-btn--success',
        'cup-variant-product__add-btn--error',
        'cup-variant-product__add-btn--added'
      );
      button.disabled = false;

      const buttonText = button.querySelector('.cup-variant-product__add-text');
      if (buttonText) buttonText.textContent = 'Add to Cart';
    }
  
    showCupVariantSelector(quantity) {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    // Update modal title and setup
    const title = modal.querySelector('[data-cup-variant-title]');
    const subtitle = modal.querySelector('[data-cup-variant-subtitle]');
    
    if (title) {
      title.textContent = `Choose Your ${quantity} FREE Cup${quantity !== 1 ? 's' : ''}`;
    }
    if (subtitle) {
      subtitle.textContent = `Select the cup variants you'd like to add to your cart`;
    }

    // Setup modal interactions
    this.setupCupVariantModal(quantity);
    
    // Show modal
    modal.style.display = 'block';
    setTimeout(() => modal.classList.add('is-active'), 10);
  }

  setupCupVariantModal(maxCups) {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    const variants = modal.querySelectorAll('[data-cup-variant-option]');
    const totalCupsSpan = modal.querySelector('[data-cup-variant-total]');
    const remainingSpan = modal.querySelector('[data-cup-variant-remaining]');
    const addButton = modal.querySelector('[data-cup-variant-add]');
    const cancelButton = modal.querySelector('[data-cup-variant-cancel]');
    const closeButton = modal.querySelector('[data-cup-variant-close]');
    const backdrop = modal.querySelector('[data-cup-variant-backdrop]');
    const selectAllBtn = modal.querySelector('[data-select-all-cups]');
    const clearSelectionBtn = modal.querySelector('[data-clear-selection]');

    let totalSelected = 0;

    // Reset all variants
    variants.forEach(variant => {
      const qtyInput = variant.querySelector('[data-cup-variant-quantity]');
      const decreaseBtn = variant.querySelector('[data-cup-variant-decrease]');
      const increaseBtn = variant.querySelector('[data-cup-variant-increase]');
      
      qtyInput.value = 0;
      totalSelected = 0;
      
      // Quantity controls
      decreaseBtn.onclick = () => this.updateVariantQuantity(variant, -1, maxCups);
      increaseBtn.onclick = () => this.updateVariantQuantity(variant, 1, maxCups);
      qtyInput.onchange = () => this.updateVariantQuantityFromInput(variant, maxCups);
    });

    // Update summary
    this.updateVariantSummary(totalSelected, maxCups);

    // Event listeners
    addButton.onclick = () => this.addSelectedCups();
    cancelButton.onclick = () => this.closeCupVariantModal();
    closeButton.onclick = () => this.closeCupVariantModal();
    backdrop.onclick = () => this.closeCupVariantModal();
    selectAllBtn.onclick = () => this.selectAllCups(maxCups);
    clearSelectionBtn.onclick = () => this.clearCupSelection();

    // Close on escape key
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  updateVariantQuantity(variant, change, maxCups) {
    const qtyInput = variant.querySelector('[data-cup-variant-quantity]');
    const currentQty = parseInt(qtyInput.value) || 0;
    const newQty = Math.max(0, Math.min(maxCups, currentQty + change));
    
    qtyInput.value = newQty;
    this.updateVariantSummary();
  }

  updateVariantQuantityFromInput(variant, maxCups) {
    const qtyInput = variant.querySelector('[data-cup-variant-quantity]');
    const newQty = Math.max(0, Math.min(maxCups, parseInt(qtyInput.value) || 0));
    qtyInput.value = newQty;
    this.updateVariantSummary();
  }

  updateVariantSummary() {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    const variants = modal.querySelectorAll('[data-cup-variant-option]');
    const totalCupsSpan = modal.querySelector('[data-cup-variant-total]');
    const remainingSpan = modal.querySelector('[data-cup-variant-remaining]');
    const addButton = modal.querySelector('[data-cup-variant-add]');

    let totalSelected = 0;
    variants.forEach(variant => {
      const qty = parseInt(variant.querySelector('[data-cup-variant-quantity]').value) || 0;
      totalSelected += qty;
    });

    const tubCount = parseInt(this.promotionBanner.dataset.tubItems || '0');
    const cupCount = parseInt(this.promotionBanner.dataset.cupItems || '0');
    const maxCups = tubCount - cupCount;
    const remaining = maxCups - totalSelected;

    if (totalCupsSpan) {
      const cupText = this.formatCupText(totalSelected, { includeCount: false });
      totalCupsSpan.textContent = `${totalSelected} ${cupText} selected`;
    }
    if (remainingSpan) {
      remainingSpan.textContent = remaining > 0 ? `${remaining} more available` : 'Maximum reached';
    }
    if (addButton) {
      addButton.disabled = totalSelected === 0;
    }
  }

  async addSelectedCups() {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    const variants = modal.querySelectorAll('[data-cup-variant-option]');
    const progressContainer = modal.querySelector('[data-cup-variant-progress]');
    const progressFill = modal.querySelector('[data-cup-variant-progress-fill]');
    const progressText = modal.querySelector('[data-cup-variant-progress-text]');

    // Show progress
    progressContainer.style.display = 'block';
    progressFill.style.width = '0%';
    progressText.textContent = 'Adding cups to cart...';

    let totalAdded = 0;
    let totalToAdd = 0;

    // Count total items to add
    variants.forEach(variant => {
      const qty = parseInt(variant.querySelector('[data-cup-variant-quantity]').value) || 0;
      totalToAdd += qty;
    });

    if (totalToAdd === 0) return;

    try {
      // Add each variant
      for (const variant of variants) {
        const qty = parseInt(variant.querySelector('[data-cup-variant-quantity]').value) || 0;
        if (qty > 0) {
          const variantId = variant.dataset.variantId;
          await this.addCupsToCart(variantId, qty);
          totalAdded += qty;
          
          // Update progress
          const progress = (totalAdded / totalToAdd) * 100;
          progressFill.style.width = `${progress}%`;
          progressText.textContent = `Added ${totalAdded} of ${totalToAdd} cups...`;
        }
      }

      // Complete
      progressText.textContent = `Successfully added ${totalAdded} cups!`;
      setTimeout(() => {
        this.closeCupVariantModal();
        this.updatePromotionStatus();
      }, 1000);

    } catch (error) {
      console.error('Error adding selected cups:', error);
      progressText.textContent = 'Error adding cups. Please try again.';
    }
  }

  closeCupVariantModal() {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    modal.classList.remove('is-active');
    setTimeout(() => {
      modal.style.display = 'none';
    }, 300);

    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      this.closeCupVariantModal();
    }
  }


  selectAllCups(maxCups) {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    const variants = modal.querySelectorAll('[data-cup-variant-option]');
    let remainingCups = maxCups;

    // Distribute cups evenly across available variants
    variants.forEach(variant => {
      const qtyInput = variant.querySelector('[data-cup-variant-quantity]');
      if (remainingCups > 0) {
        const cupsToAdd = Math.min(1, remainingCups); // Add 1 cup per variant
        qtyInput.value = cupsToAdd;
        remainingCups -= cupsToAdd;
      } else {
        qtyInput.value = 0;
      }
    });

    this.updateVariantSummary();
  }

  clearCupSelection() {
    const modal = document.querySelector('[data-cup-variant-modal]');
    if (!modal) return;

    const variants = modal.querySelectorAll('[data-cup-variant-option]');
    variants.forEach(variant => {
      const qtyInput = variant.querySelector('[data-cup-variant-quantity]');
      qtyInput.value = 0;
    });

    this.updateVariantSummary();
  }


  
    async addCupsToCart(variantId, quantity) {
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) throw new Error('Failed to add cups');

        const data = await response.json();

        // Trigger cart update events (use cart:item-added to avoid triggering cup button success)
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));

      } catch (error) {
        console.error('Error adding cups to cart:', error);
      }
    }
  
    async getCartData(forceRefresh = false) {
      // Short-lived cache to avoid duplicate /cart.json requests during rapid updates
      const now = Date.now();
      if (!forceRefresh && this.cartDataCache.data && (now - this.cartDataCache.fetchedAt) < this.cacheTTL) {
        return this.cartDataCache.data;
      }
      try {
        const response = await this.fetchWithTimeout('/cart.json', {}, 5000);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        this.cartDataCache = { data, fetchedAt: Date.now() };
        return data;
      } catch (error) {
        console.error('Error fetching cart data:', error);
        return null;
      }
    }

    async refreshModalAvailability() {
      const selector = document.querySelector('.cup-variant-selector.is-active');
      if (!selector) return;

      try {
        // Show loading state for the entire modal
        this.showModalLoading();

        // Get fresh cart data with timeout protection
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Modal refresh timeout')), 8000);
        });
        const cartDataPromise = this.getCartData();
        const cartData = await Promise.race([cartDataPromise, timeoutPromise]);
        if (!cartData) return;

        // Re-calculate everything
        const tubCount = await this.countTubItems(cartData);
        const cupCount = await this.countCupItems(cartData);
        const cupsAvailable = Math.max(0, tubCount - cupCount);

        // Detect which regions are in the cart
        const cartRegions = await this.detectCartRegions(cartData);

        // Update modal subtitle
        this.updateModalSubtitle(cupsAvailable);
        
        // Update product availability and button states (with region filtering)
        await this.updateProductAvailability(cupsAvailable, cartRegions);
        
        // Hide loading state
        this.hideModalLoading();

      } catch (error) {
        console.error('❌ Error refreshing modal availability:', error);
        this.hideModalLoading();
      }
    }

    showModalLoading() {
      const selector = document.querySelector('.cup-variant-selector.is-active');
      if (!selector) return;

      // Skip shimmer entirely if a form submit is in progress
      // The per-button loading animation handles feedback in that case
      if (this.isUpdating) return;

      // Hide empty state container immediately to prevent flickering
      const noCupsContainer = selector.querySelector('[data-no-cups-container]');
      if (noCupsContainer) {
        noCupsContainer.style.display = 'none';
      }

      // Add shimmer effect to all products AND disable all buttons
      const products = selector.querySelectorAll('.cup-variant-product');
      products.forEach(product => {
        product.classList.add('cup-progress-loading');

        // Disable all add buttons during loading
        const addButton = product.querySelector('[data-cup-variant-add]');
        if (addButton) {
          addButton.disabled = true;
          addButton.classList.add('cup-variant-product__add-btn--loading-disabled');
        }
      });
    }

    hideModalLoading() {
      const selector = document.querySelector('.cup-variant-selector.is-active');
      if (!selector) return;

      // Remove shimmer effect from products
      const products = selector.querySelectorAll('.cup-variant-product');
      products.forEach(product => {
        product.classList.remove('cup-progress-loading');

        const addButton = product.querySelector('[data-cup-variant-add]');
        if (addButton) {
          addButton.classList.remove('cup-variant-product__add-btn--loading-disabled');
          // Don't re-enable buttons mid-animation
          if (!addButton.classList.contains('cup-variant-product__add-btn--loading') &&
              !addButton.classList.contains('cup-variant-product__add-btn--success')) {
            // Let updateProductAvailability handle the enabled/disabled state
          }
        }
      });
    }

    updateModalSubtitle(cupsAvailable) {
      const modalSubtitle = document.querySelector('[data-cup-selector-subtitle]');
      if (!modalSubtitle) return;
      
      const container = document.querySelector('[data-has-cups]');
      const productsContainer = document.querySelector('.cup-variant-selector__products');
      const noCupsContainer = document.querySelector('[data-no-cups-container]');
      
      // Check if there are any products in the collection
      const allProducts = document.querySelectorAll('.cup-variant-product');
      const hasProducts = allProducts.length > 0;
      
      if (cupsAvailable > 0 && hasProducts) {
        const cupText = this.formatCupText(cupsAvailable);
        modalSubtitle.textContent = `${cupText} available`;
        
        // Update data attribute
        if (container) {
          container.setAttribute('data-has-cups', cupsAvailable);
        }
        
        // Show products, hide no cups message
        if (productsContainer) productsContainer.style.display = 'grid';
        if (noCupsContainer) noCupsContainer.style.display = 'none';
        
      } else {
        modalSubtitle.textContent = 'Browse available cups';
        
        // Update data attribute
        if (container) {
          container.setAttribute('data-has-cups', '0');
        }
        
        // Hide products, show no cups message
        if (productsContainer) productsContainer.style.display = 'none';
        if (noCupsContainer) noCupsContainer.style.display = 'block';
      }
    }

    async updateProductAvailability(cupsAvailable, cartRegions = []) {
      const allProducts = document.querySelectorAll('.cup-variant-product');
      const productsContainer = document.querySelector('.cup-variant-selector__products');
      const noCupsContainer = document.querySelector('[data-no-cups-container]');
      
      let visibleProductCount = 0;
      
      // Filter products based on region compatibility
      for (const product of allProducts) {
        const addButton = product.querySelector('[data-cup-variant-add]');
        const quantityInput = product.querySelector('[data-quantity-input]');
        if (!addButton) continue;
        
        // Get product region from data attribute (set by Liquid template)
        const productRegion = product.getAttribute('data-region');
        
        // Determine if product is compatible with cart regions
        const isRegionCompatible = this.isProductRegionCompatible(productRegion, cartRegions);
        
        // Hide incompatible products
        if (!isRegionCompatible) {
          product.style.display = 'none';
          continue;
        } else {
          product.style.display = 'block';
          visibleProductCount++;
        }
        
        // Update data attributes
        product.setAttribute('data-cups-available', cupsAvailable);
        
        // Update quantity input max value
        if (quantityInput) {
          quantityInput.setAttribute('max', cupsAvailable);
          // If current value exceeds available, reset to max available
          const currentValue = parseInt(quantityInput.value || '1');
          if (currentValue > cupsAvailable && cupsAvailable > 0) {
            quantityInput.value = cupsAvailable;
          } else if (cupsAvailable === 0) {
            quantityInput.value = 1; // Reset to 1 when none available
          }
        }
        
        // Skip buttons mid-animation (loading/success) to preserve their state
        const isButtonBusy = addButton.classList.contains('cup-variant-product__add-btn--loading') ||
                             addButton.classList.contains('cup-variant-product__add-btn--success');

        // Enable/disable button based on available cups
        if (cupsAvailable > 0) {
          if (!isButtonBusy) {
            addButton.disabled = false;
            addButton.classList.remove('cup-variant-product__add-btn--disabled');

            const addText = addButton.querySelector('.cup-variant-product__add-text');
            if (addText) {
              addText.textContent = 'Add to Cart';
            }
          }

        } else {
          // Disable button - no cups available (always override, even mid-animation)
          addButton.disabled = true;
          addButton.classList.add('cup-variant-product__add-btn--disabled');

          const addText = addButton.querySelector('.cup-variant-product__add-text');
          if (addText) {
            addText.textContent = 'All Claimed';
          }
        }
      }
      
      // Show/hide containers based on visible products and cups available
      // If no products are visible OR no cups available, show no-cups message
      if (visibleProductCount === 0 || cupsAvailable <= 0) {
        if (productsContainer) productsContainer.style.display = 'none';
        if (noCupsContainer) noCupsContainer.style.display = 'block';
      } else {
        if (productsContainer) productsContainer.style.display = 'grid';
        if (noCupsContainer) noCupsContainer.style.display = 'none';
      }
    }

    isProductRegionCompatible(productRegion, cartRegions) {
      // If no regions detected in cart, something went wrong - default to showing products
      if (!cartRegions || cartRegions.length === 0) {
        return true;
      }
      
      // Product region should always be set (either specific region or 'international')
      if (!productRegion) {
        productRegion = 'international';
      }
      
      // Check if product region matches any cart region
      return cartRegions.includes(productRegion);
    }
  }
  
  // Initialize
  window.cupPromotionTracker = new CupPromotionTracker();

  // Debug function for testing modal refresh
  window.testModalRefresh = function() {
    console.log('🧪 Manual modal refresh test...');
    if (window.cupPromotionTracker) {
      window.cupPromotionTracker.refreshModalAvailability();
    } else {
      console.error('❌ Cup promotion tracker not found');
    }
  };

  // Debug function to check current state
  window.debugCupPromotion = function() {
    console.log('🔍 Cup Promotion Debug:');
    const banner = document.querySelector('[data-cup-promotion]');
    console.log('- Banner found:', !!banner);
    console.log('- Banner data:', banner?.dataset);
    console.log('- Tracker:', window.cupPromotionTracker);
    console.log('- Modal found:', !!document.querySelector('[data-cup-variant-selector]'));
    if (window.cupPromotionTracker) {
      window.cupPromotionTracker.updatePromotionStatus();
    }
  };
